import {
  Flex,
  Box,
  Text,
  Link,
  TransparentButton,
  Divider,
  Stack,
} from "flicket-ui";

import { ReleaseType } from "~graphql/sdk";
import { CreateOrderFn } from "~hooks";
import {
  INormalisedData,
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import { ExperienceSalesFooter } from "~features/nonseated-reservation/components/ExperienceSalesFooter/ExperienceSalesFooter";
import { ReservationList } from "~components/reservation/non-seated";
import { FixedRatioImage, Icon } from "~components/common";
import HeaderAlertBanner, {
  useShowHeaderBanner,
} from "~components/common/common.HeaderAlertBanner";
import { useRouter } from "next/router";
import { DefaultTheme, ThemeProps } from "styled-components";
import { ReactNode, useEffect } from "react";
import StickySection from "~components/common/StickySection";
import { CaretLeft } from "phosphor-react";
import { Footer } from "~components";
import { addQueryParams } from "~lib/helpers/queryParams";

type NonSeatedProps = {
  data: INormalisedData;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];
  createOrder: CreateOrderFn;
  isMembership: boolean;
  isAdmin: boolean;
  releaseId: string;
  releaseNotes: string;
  allowPublicComp: boolean;
  isResaleRelease: boolean;
  releaseType: ReleaseType;
  requiresAuth: boolean;
};

export const NonSeatedReservationPage = ({
  addons,
  data,
  zones,
  createOrder,
  isMembership = false,
  isAdmin,
  releaseId,
  releaseNotes,
  allowPublicComp,
  isResaleRelease,
  releaseType,
  requiresAuth = false,
}: NonSeatedProps) => {
  const router = useRouter();

  function onClickBack() {
    void router.push(`/events/${data.id}`);
  }

  return (
    <ScrollWindow>
      {/* <Box mb={0}> */}
      <Header onClickBack={onClickBack} />

      {data.banner && (
        <Box
          width={1}
          px={{ _: 0, sm: 2 }}
          maxW={{ sm: 960 }}
          margin={"auto" as any}
          mb={4}
        >
          <Box position="relative">
            <FixedRatioImage
              image={data.banner}
              alt={data.name}
              aspectRatio={"5 / 2"}
              width={{ _: "100%" }}
              borderRadius={{ sm: "sm" }}
            />

            <Link
              href={`/events/${data.id}`}
              display="inline-flex"
              justifyContent="center"
              position="absolute"
              top={2}
              left={2}
              width={40}
              height={40}
              borderRadius="full"
              css={`
                transition: none;
                background-color: rgba(255, 255, 255, 0.92);

                &:hover {
                  background-color: rgba(255, 255, 255, 0.9);
                }
              `}
            >
              <Icon icon={<CaretLeft size={20} weight="bold" />} color="N800" />
            </Link>
          </Box>
        </Box>
      )}

      <Box width={1} px={2} maxW={{ sm: 960 }} margin={"auto" as any} mb={3}>
        <Text variant="header.L">{data.name}</Text>
        <Text variant="regular">
          {data.displayDate} • {data.location}
        </Text>
      </Box>

      <StickyZoneTabs zones={zones} />

      <Box width={1} px={2} maxW={{ sm: 960 }} margin={"auto" as any}>
        <ReservationList
          addons={addons}
          data={data}
          zones={zones}
          createOrder={createOrder}
          isMembership={isMembership}
          releaseNotes={releaseNotes}
          isDigital={data.isDigital}
          allowPublicComp={allowPublicComp}
          isResaleRelease={isResaleRelease}
          releaseType={releaseType}
          requiresAuth={requiresAuth}
        />
      </Box>

      <ExperienceSalesFooter
        id={data.id}
        releaseId={releaseId}
        hideFooterDuringMode={["no-selection"]}
      />
      <Footer
        pb={"800px" as any}
        narrow={true}
        display={{ _: "flex", md: "flex" }}
      />
    </ScrollWindow>
  );
};

function ScrollWindow({ children }: { children: ReactNode }) {
  useEffect(() => {
    const bodyEl = document.querySelector("body");
    bodyEl.classList.add("no-scroll");
    return () => bodyEl.classList.remove("no-scroll");
  }, []);

  return (
    <Box
      id="scroll-window"
      width={1}
      overflowY="auto"
      h="100dvh"
      position="relative"
    >
      {children}
    </Box>
  );
}

const TABS_HEIGHT = 48;

function StickyZoneTabs({ zones }: { zones: IReleaseZone[] }) {
  const router = useRouter();
  const { bannerHeight, isBannerVisible } = useShowHeaderBanner();

  function handleClick(id: string) {
    addQueryParams(router, { zoneId: id });

    const offset = (isBannerVisible ? bannerHeight : 0) + TABS_HEIGHT + 32;
    const scrollWindowEl = document.getElementById("scroll-window");

    scrollWindowEl.scrollTo({
      behavior: "smooth",
      top:
        scrollWindowEl.scrollTop +
        document.getElementById(id)?.getBoundingClientRect().top -
        offset,
    });
  }

  return (
    <StickySection>
      {({ isSticky }) => (
        <Box
          boxShadow={isSticky ? "card" : undefined}
          backgroundColor="white"
          mb={8}
        >
          <Box px={2} maxW={{ sm: 960 }} margin={"auto" as any}>
            <Box
              borderBottom="1px solid"
              borderBottomColor={isSticky ? "transparent" : "N200"}
            >
              <Stack
                direction="horizontal"
                gap={2}
                w="100%"
                overflowX="auto"
                pr={2}
                position={"relative"}
                // Adjust for the bottom border overlap
                top={"1px" as any}
                zIndex={1}
              >
                {zones
                  .filter((z) => {
                    // TODO: incorporate "isAvailableForSelection" into the filter
                    // Hoist to top level data transforms?
                    return (
                      z.isActive && z.ticketTypes.some((tt) => tt.isActive)
                    );
                  })
                  .map((zone, i) => {
                    const isSelected =
                      (!router.query.zoneId && i === 0) ||
                      router.query.zoneId === zone.id;

                    return (
                      <TabItem
                        key={zone.id}
                        name={zone.name}
                        isSelected={isSelected}
                        onClick={() => handleClick(zone.id)}
                      />
                    );
                  })}

                <TabItem
                  key={"addons"}
                  name="Add-ons"
                  isSelected={router.query.zoneId === "add-ons"}
                  onClick={() => handleClick("add-ons")}
                />
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </StickySection>
  );
}

function TabItem({
  onClick,
  name,
  isSelected,
}: {
  onClick: () => void;
  name: string;
  isSelected: boolean;
}) {
  return (
    <Flex onClick={onClick} alignItems="flex-end" flexWrap="nowrap">
      <Text
        variant="header.XS"
        cursor="pointer"
        css={`
          text-wrap: nowrap;
          &:hover {
            border-color: ${(p: ThemeProps<DefaultTheme>) =>
              p.theme.colors.N800};
          }
        `}
        py={2}
        borderBottom="3px solid"
        borderColor={isSelected ? "N800" : "transparent"}
      >
        {name}
      </Text>
    </Flex>
  );
}

function Header({ onClickBack }: { onClickBack: () => void }) {
  const { isBannerVisible, bannerHeight } = useShowHeaderBanner();

  return (
    <>
      <Box
        zIndex="banner"
        css={`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
        `}
      >
        <HeaderAlertBanner />
      </Box>

      <Box pb={{ sm: 3 }} />
    </>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <Box width={1} px={2} maxW={{ sm: 960 }} margin={"auto" as any}>
      {children}
    </Box>
  );
}
